/**
 * Assets Unified Hook
 * 
 * Queries assets_unified_view to get all assets (accounts, holdings, manual)
 * in a single flat list for the Assets page.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';

// Unified asset type from the view
export interface UnifiedAsset {
  source_type: 'account' | 'connected' | 'manual';
  id: string;
  user_id: string;
  asset_name: string;
  symbol: string | null;
  asset_type: 'cash' | 'equity' | 'fixed_income' | 'alternative' | 'unclassified' | 'multi_asset';
  quantity: number | null;
  market_value: number;
  currency: string;
  registered: string | null;
  description: string | null;
  pricing_method: string | null;
  as_of: string;
  provider_name: string | null;
  account_identifier: string | null;
  institution_id: string | null;
  institution_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface UseAssetsUnifiedOptions {
  // Filter to only show UI-facing asset types (exclude unclassified/multi_asset)
  excludeBackendTypes?: boolean;
  // Optional additional filters
  assetTypes?: string[];
  sourceTypes?: ('account' | 'connected' | 'manual')[];
}

export function useAssetsUnified(options: UseAssetsUnifiedOptions = {}) {
  const { user } = useAuthStore();
  const {
    excludeBackendTypes = true,
    assetTypes,
    sourceTypes,
  } = options;

  const queryKey = ['assets-unified', user?.id, options];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Note: assets_unified_view is not in generated types yet
      // Using 'as any' to bypass TypeScript errors until types are regenerated
      let query = supabase
        .from('assets_unified_view' as any)
        .select('*')
        .eq('user_id', user.id);

      // IMPORTANT: Keep accounts visible even if classified as multi_asset.
      // We avoid server-side filtering here to allow OR logic (account OR allowed asset types).

      // Apply asset type filter if provided
      if (assetTypes && assetTypes.length > 0) {
        query = query.in('asset_type', assetTypes);
      }

      // Apply source type filter if provided
      if (sourceTypes && sourceTypes.length > 0) {
        query = query.in('source_type', sourceTypes);
      }

      // Order by market value descending
      query = query.order('market_value', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching unified assets:', error);
        throw error;
      }

      // Apply backend-type filter client-side:
      // - Always allow accounts
      // - Allow UI-facing classes for non-accounts (cash, equity, fixed_income, alternative, multi_asset)
      // - Additionally include negative unclassified holdings to surface liabilities (e.g., options in margin)
      const raw = (data || []) as unknown as UnifiedAsset[];
      const uiAssetTypes = ['cash', 'equity', 'fixed_income', 'alternative', 'multi_asset'] as const;
      const result = excludeBackendTypes
        ? raw.filter(a => {
            if (a.source_type === 'account') return true;
            if (uiAssetTypes.includes(a.asset_type as any)) return true;
            // Include negative unclassified holdings (non-accounts implied here)
            return a.asset_type === 'unclassified' && (a.market_value || 0) < 0;
          })
        : raw;

      return result as unknown as UnifiedAsset[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Calculate stats
  const stats = useMemo(() => {
    if (!data) {
      return {
        totalValue: 0,
        totalCount: 0,
        byAssetType: {},
        bySourceType: {},
        byCurrency: {},
      };
    }

    // Determine accounts that have holdings to avoid double counting in totals
    const accountsWithHoldings = new Set<string>();
    data.forEach(asset => {
      if (asset.source_type === 'connected' && asset.account_identifier) {
        accountsWithHoldings.add(asset.account_identifier);
      }
    });

    const shouldIncludeInTotals = (asset: UnifiedAsset) => {
      if (asset.source_type !== 'account') return true;
      if (!asset.account_identifier) return true;
      return !accountsWithHoldings.has(asset.account_identifier);
    };

    let totalValue = 0;
    const totalCount = data.length; // keep count as displayed row count

    // Group by asset type
    const byAssetType: Record<string, { count: number; value: number }> = {};
    // Group by source type
    const bySourceType: Record<string, { count: number; value: number }> = {};
    // Group by currency
    const byCurrency: Record<string, { count: number; value: number }> = {};

    data.forEach(asset => {
      const valueToAdd = shouldIncludeInTotals(asset) ? (asset.market_value || 0) : 0;
      totalValue += valueToAdd;

      // asset type grouping
      if (!byAssetType[asset.asset_type]) {
        byAssetType[asset.asset_type] = { count: 0, value: 0 };
      }
      byAssetType[asset.asset_type].count++;
      byAssetType[asset.asset_type].value += valueToAdd;

      // source type grouping
      if (!bySourceType[asset.source_type]) {
        bySourceType[asset.source_type] = { count: 0, value: 0 };
      }
      bySourceType[asset.source_type].count++;
      bySourceType[asset.source_type].value += valueToAdd;

      // currency grouping
      if (!byCurrency[asset.currency]) {
        byCurrency[asset.currency] = { count: 0, value: 0 };
      }
      byCurrency[asset.currency].count++;
      byCurrency[asset.currency].value += valueToAdd;
    });

    return {
      totalValue,
      totalCount,
      byAssetType,
      bySourceType,
      byCurrency,
    };
  }, [data]);

  return {
    assets: data || [],
    isLoading,
    error,
    refetch,
    stats,
  };
}

// Helper to useMemo
import { useMemo } from 'react';
